import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ShoppingCart, X, Plus, Minus, Truck, Clock, Download, Package, Mail, ChevronRight, Send, CreditCard, FileText } from 'lucide-react';

import { usePromoCart } from './promo/promoCart';
import { PromoOfferCard } from './promo/PromoOfferCard';
import { PromoCartDrawer, PromoPaymentReturn } from './promo/PromoCartDrawer';
import { PromoHeader, PromoFaq, MobileBuyBar } from './promo/PromoPageChrome';
import { trackPromo } from '../lib/promoAttribution';
import { trackMeta } from '../lib/metaPixel';
import { fetchJsonRetry } from '../lib/clientLog';
import { OfferRequestModal } from './promo/OfferRequestModal';

const PROMO_CSS = `@keyframes promoCardIn{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}@keyframes promoFade{from{opacity:.2}to{opacity:1}}.promo-card-in{animation:promoCardIn .55s cubic-bezier(.2,.7,.2,1) both}@media (prefers-reduced-motion:reduce){.promo-card-in{animation:none}}.promo-scroll{scrollbar-width:thin;scrollbar-color:#333 transparent}`;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Countdown hook
function useCountdown(endDate) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: true });
  useEffect(() => {
    if (!endDate) return;
    const tick = () => {
      const diff = new Date(endDate).getTime() - Date.now();
      if (diff <= 0) { setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: true }); return; }
      setTimeLeft({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
        expired: false,
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endDate]);
  return timeLeft;
}

function CountdownDisplay({ endDate }) {
  const t = useCountdown(endDate);
  if (t.expired) return <span className="text-red-400 text-sm font-medium">Offre expirée</span>;
  return (
    <div className="flex gap-2" data-testid="countdown-display">
      {[['J', t.days], ['H', t.hours], ['M', t.minutes], ['S', t.seconds]].map(([label, val]) => (
        <div key={label} className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-center min-w-[52px]">
          <p className="text-2xl font-bold text-amber-400 tabular-nums">{String(val).padStart(2, '0')}</p>
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{label}</p>
        </div>
      ))}
    </div>
  );
}

// Block renderers
function BannerBlock({ block }) {
  const c = block.content || {};
  return (
    <div data-testid={`promo-block-banner-${block.id}`} className="relative overflow-hidden rounded-2xl" style={{ backgroundColor: c.bg_color || '#0A0A0A' }}>
      {c.image_url && <img src={c.image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />}
      <div className="relative z-10 py-16 md:py-24 px-8 text-center">
        <h2 className="text-3xl md:text-5xl font-bold text-white mb-3">{block.title}</h2>
        {c.subtitle && <p className="text-lg md:text-xl text-zinc-300 max-w-2xl mx-auto">{c.subtitle}</p>}
        {c.cta_text && (
          <a href={c.cta_link || '#'} className="inline-block mt-6 bg-amber-500 text-black px-8 py-3 rounded-lg font-bold hover:bg-amber-400 transition-colors">
            {c.cta_text} <ChevronRight className="w-4 h-4 inline ml-1" />
          </a>
        )}
      </div>
    </div>
  );
}

function LinkedFlashBlock({ block, offer, cart, highlighted, onAdded, onEdit, onRequest, qty, setQty }) {
  return (
    <div data-testid={`promo-block-flash-${block.id}`} className="space-y-0">
      <div className="bg-red-600 text-white text-center py-2 text-sm font-bold tracking-wider flex items-center justify-center gap-2 rounded-t-2xl">
        <Clock className="w-4 h-4" /> {block.title || 'OFFRE FLASH — Temps limité'}
      </div>
      {block.end_date && <div className="bg-zinc-900 border-x border-zinc-800 py-4 flex justify-center"><CountdownDisplay endDate={block.end_date} /></div>}
      <PromoOfferCard offer={offer} cart={cart} highlighted={highlighted} onAdded={onAdded} onEdit={onEdit} onRequest={onRequest} qty={qty} setQty={setQty} />
    </div>
  );
}

function FlashBlock({ block, onOrder, onEmailRequest }) {
  const c = block.content || {};
  const discount = c.original_price > 0 ? Math.round((1 - c.promo_price / c.original_price) * 100) : 0;
  return (
    <div data-testid={`promo-block-flash-${block.id}`} className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
      <div className="bg-red-600 text-white text-center py-2 text-sm font-bold tracking-wider flex items-center justify-center gap-2">
        <Clock className="w-4 h-4" /> OFFRE FLASH — Temps limité
      </div>
      <div className="p-6 md:p-8 flex flex-col md:flex-row gap-8 items-center">
        {c.image_url && (
          <div className="w-full md:w-1/3 aspect-square bg-white rounded-xl flex items-center justify-center p-4 relative">
            {discount > 0 && <span className="absolute top-3 left-3 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-lg z-10">-{discount}%</span>}
            <img src={c.image_url} alt={c.product_name} className="max-h-full max-w-full object-contain" />
          </div>
        )}
        <div className="flex-1 text-center md:text-left">
          <h3 className="text-2xl font-bold text-white mb-2">{block.title || c.product_name}</h3>
          {c.description && <p className="text-zinc-400 mb-4">{c.description}</p>}
          <div className="flex items-baseline gap-3 justify-center md:justify-start mb-4">
            {c.original_price > 0 && <span className="text-lg text-zinc-500 line-through">CHF {Number(c.original_price).toFixed(2)}</span>}
            <span className="text-3xl font-bold text-amber-400">CHF {Number(c.promo_price || 0).toFixed(2)}</span>
          </div>
          {block.end_date && <div className="mb-6 flex justify-center md:justify-start"><CountdownDisplay endDate={block.end_date} /></div>}
          <div className="flex gap-3 justify-center md:justify-start flex-wrap">
            <button onClick={() => onOrder(block)} className="bg-amber-500 text-black px-8 py-3 rounded-lg font-bold hover:bg-amber-400 transition-colors flex items-center gap-2" data-testid="flash-order-btn">
              <ShoppingCart className="w-4 h-4" /> Commander
            </button>
            <button onClick={() => onEmailRequest(block)} className="border border-zinc-600 text-zinc-300 px-6 py-3 rounded-lg font-medium hover:bg-zinc-800 transition-colors flex items-center gap-2" data-testid="flash-email-btn">
              <Mail className="w-4 h-4" /> Demander par email
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function GridBlock({ block, onOrder, onEmailRequest }) {
  const c = block.content || {};
  const gridProducts = c.products || [];
  return (
    <div data-testid={`promo-block-grid-${block.id}`} className="space-y-6">
      {block.title && <h2 className="text-2xl font-bold text-white text-center">{block.title}</h2>}
      <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {gridProducts.map((p, i) => {
          const discount = p.original_price > 0 && p.promo_price < p.original_price ? Math.round((1 - p.promo_price / p.original_price) * 100) : 0;
          return (
            <div key={i} className="bg-zinc-900 rounded-2xl overflow-hidden border border-zinc-800 hover:border-zinc-600 transition-all group">
              <div className="aspect-square bg-white p-4 flex items-center justify-center relative">
                {discount > 0 && <span className="absolute top-3 left-3 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-lg z-10">-{discount}%</span>}
                {p.badge && <span className="absolute top-3 right-3 bg-amber-500 text-black text-[10px] font-bold px-2 py-0.5 rounded-full">{p.badge}</span>}
                {p.image_url ? <img loading="lazy" src={p.image_url} alt={p.name} className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform" /> : <div className="w-24 h-24 bg-zinc-100 rounded-xl" />}
              </div>
              <div className="p-5">
                <h3 className="font-bold text-white mb-2 line-clamp-2">{p.name}</h3>
                <div className="flex items-baseline gap-2 mb-4">
                  {p.original_price > 0 && p.promo_price < p.original_price && <span className="text-sm text-zinc-500 line-through">CHF {Number(p.original_price).toFixed(2)}</span>}
                  <span className="text-xl font-bold text-white">CHF {Number(p.promo_price || p.original_price || 0).toFixed(2)}</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => onOrder({ ...block, _singleProduct: p })} className="flex-1 bg-amber-500 text-black py-2.5 rounded-lg font-bold hover:bg-amber-400 transition-colors flex items-center justify-center gap-2 text-sm" data-testid={`grid-order-btn-${i}`}>
                    <ShoppingCart className="w-4 h-4" /> Commander
                  </button>
                  <button onClick={() => onEmailRequest({ ...block, _singleProduct: p })} className="w-10 h-10 border border-zinc-700 rounded-lg flex items-center justify-center text-zinc-400 hover:bg-zinc-800 hover:text-white transition-colors" data-testid={`grid-email-btn-${i}`}>
                    <Mail className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PdfBlock({ block }) {
  const c = block.content || {};
  const isPdf = c.file_url?.toLowerCase().endsWith('.pdf');
  return (
    <div data-testid={`promo-block-pdf-${block.id}`} className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
      <div className="p-6 md:p-8">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 bg-green-600/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <FileText className="w-6 h-6 text-green-400" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">{block.title}</h3>
            {c.description && <p className="text-zinc-400 mt-1">{c.description}</p>}
          </div>
        </div>
        {isPdf && c.file_url && (
          <div className="bg-white rounded-xl overflow-hidden mb-4" style={{ height: '500px' }}>
            <iframe src={`${BACKEND_URL}${c.file_url}`} title={block.title} className="w-full h-full" />
          </div>
        )}
        {!isPdf && c.file_url && (
          <div className="rounded-xl overflow-hidden mb-4">
            <img src={c.file_url.startsWith('/') ? `${BACKEND_URL}${c.file_url}` : c.file_url} alt={block.title} className="w-full rounded-xl" />
          </div>
        )}
        {c.cta_text && (
          <a href={c.file_url?.startsWith('/') ? `${BACKEND_URL}${c.file_url}` : c.file_url} target="_blank" rel="noopener noreferrer" download
            className="inline-flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-green-500 transition-colors" data-testid="pdf-download-btn">
            <Download className="w-4 h-4" /> {c.cta_text}
          </a>
        )}
      </div>
    </div>
  );
}

function BundleBlock({ block, onOrder, onEmailRequest }) {
  const c = block.content || {};
  const saving = (c.normal_price || 0) - (c.bundle_price || 0);
  return (
    <div data-testid={`promo-block-bundle-${block.id}`} className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
      <div className="bg-amber-500 text-black text-center py-2 text-sm font-bold tracking-wider flex items-center justify-center gap-2">
        <Package className="w-4 h-4" /> PACK PROMO
      </div>
      <div className="p-6 md:p-8 flex flex-col md:flex-row gap-8">
        {c.image_url && (
          <div className="w-full md:w-1/3 aspect-square bg-white rounded-xl flex items-center justify-center p-4">
            <img src={c.image_url} alt={block.title} className="max-h-full max-w-full object-contain" />
          </div>
        )}
        <div className="flex-1">
          <h3 className="text-2xl font-bold text-white mb-2">{block.title}</h3>
          {c.description && <p className="text-zinc-400 mb-4">{c.description}</p>}
          {(c.products || []).length > 0 && (
            <div className="space-y-2 mb-4">
              <p className="text-sm font-medium text-zinc-300">Contenu du pack :</p>
              {c.products.map((p, i) => (
                <div key={i} className="flex items-center gap-3 p-2 bg-zinc-800 rounded-lg">
                  <span className="w-7 h-7 bg-amber-500/20 rounded-full flex items-center justify-center text-amber-400 text-xs font-bold">{p.quantity || 1}x</span>
                  <span className="text-sm text-white flex-1">{p.name}</span>
                  {p.unit_price > 0 && <span className="text-xs text-zinc-500">CHF {Number(p.unit_price).toFixed(2)}/u</span>}
                </div>
              ))}
            </div>
          )}
          <div className="flex items-baseline gap-3 mb-4">
            {c.normal_price > 0 && <span className="text-lg text-zinc-500 line-through">CHF {Number(c.normal_price).toFixed(2)}</span>}
            <span className="text-3xl font-bold text-amber-400">CHF {Number(c.bundle_price || 0).toFixed(2)}</span>
            {saving > 0 && <span className="bg-green-600/20 text-green-400 text-sm px-3 py-1 rounded-full font-medium">-CHF {saving.toFixed(2)}</span>}
          </div>
          <div className="flex gap-3 flex-wrap">
            <button onClick={() => onOrder(block)} className="bg-amber-500 text-black px-8 py-3 rounded-lg font-bold hover:bg-amber-400 transition-colors flex items-center gap-2" data-testid="bundle-order-btn">
              <ShoppingCart className="w-4 h-4" /> Commander le pack
            </button>
            <button onClick={() => onEmailRequest(block)} className="border border-zinc-600 text-zinc-300 px-6 py-3 rounded-lg font-medium hover:bg-zinc-800 transition-colors flex items-center gap-2" data-testid="bundle-email-btn">
              <Mail className="w-4 h-4" /> Demander par email
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ====== MAIN COMPONENT ======
const PromoFallbackActions = ({ onRetry, onNavigate }) => (
  <div className="flex flex-wrap justify-center gap-3">
    <button onClick={onRetry} className="h-11 px-6 rounded-lg bg-amber-500 text-black font-bold hover:bg-amber-400" data-testid="promo-load-retry">Réessayer</button>
    <button onClick={() => (onNavigate ? onNavigate('boutique') : (window.location.href = '/?page=boutique'))} className="h-11 px-6 rounded-lg border border-zinc-600 text-white hover:border-zinc-400" data-testid="promo-see-shop">Voir la boutique</button>
  </div>
);

export default function PromotionsPage({ onNavigate }) {
  const preload = typeof window !== 'undefined' ? window.__LZ_PROMO__ : null;
  const [blocks, setBlocks] = useState(preload?.blocks || []);
  const [loading, setLoading] = useState(!preload?.offers?.length);
  const [loadError, setLoadError] = useState(false);
  // Order modal
  const [orderModal, setOrderModal] = useState(null);
  const [emailModal, setEmailModal] = useState(null);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', address: '', postal_code: '', city: '', quantity: 1, message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [offers, setOffers] = useState(preload?.offers || []);
  const [cartOpen, setCartOpen] = useState(false);
  const [settings, setSettings] = useState({ banner_text: preload?.settings?.banner_text || '', faq: preload?.settings?.faq || [] });
  const [qtys, setQtys] = useState({});
  const [activeSlug, setActiveSlug] = useState(null);
  const [requestOffer, setRequestOffer] = useState(null);
  const cart = usePromoCart();
  const qtyProps = (slug) => ({ qty: qtys[slug] || 1, setQty: (v) => setQtys(q => ({ ...q, [slug]: v })) });
  const viewedSlugs = useRef(new Set());
  const handleAdded = (slug, qty = 1, unitHt = 0) => {
    trackPromo('add_to_cart', { offer_slug: slug });
    trackMeta('AddToCart', { content_ids: [slug], content_type: 'product', value: Math.round(unitHt * qty * 100) / 100, currency: 'CHF', num_items: qty });
    setCartOpen(true);
  };

  useEffect(() => {
    if (!activeSlug || viewedSlugs.current.has(activeSlug)) return;
    viewedSlugs.current.add(activeSlug);
    trackPromo('offer_view', { offer_slug: activeSlug });
    const o = offers.find((x) => x.slug === activeSlug);
    trackMeta('ViewContent', { content_ids: [activeSlug], content_type: 'product', content_name: o?.name, value: o?.tiers?.[0]?.price_ht, currency: 'CHF' });
  }, [activeSlug]);
  const urlParams = new URLSearchParams(window.location.search);
  const focusSlug = urlParams.get('offre');
  const [paymentSession, setPaymentSession] = useState(urlParams.get('promo_payment') === 'success' ? urlParams.get('session_id') : null);

  const loadAll = () => {
    setLoadError(false);
    fetchJsonRetry('/api/promotions').then((p) => setBlocks(p.blocks || [])).catch(() => {});
    fetchJsonRetry('/api/promo-page/settings').then((st) => setSettings({ banner_text: st.banner_text || '', faq: st.faq || [] })).catch(() => {});
    fetchJsonRetry('/api/promo-offers')
      .then((o) => { setOffers(o.offers || []); setLoading(false); })
      .catch(() => { if (!window.__LZ_PROMO__?.offers?.length) setLoadError(true); setLoading(false); });
  };

  useEffect(() => {
    if (window.__LZ_PROMO__?.offers?.length) {
      // offres déjà pré-chargées : rafraîchissement après l'affichage pour ne pas retarder le premier produit
      const t = setTimeout(loadAll, 1500);
      return () => clearTimeout(t);
    }
    loadAll();
    return undefined;
  }, []);

  useEffect(() => {
    if (urlParams.get('promo_payment') !== 'success') trackPromo('visit', { offer_slug: focusSlug || undefined });
    if (urlParams.get('promo_payment') === 'cancel' || urlParams.get('panier') === '1') setCartOpen(true);
  }, []);

  const offerBySlug = Object.fromEntries(offers.map(o => [o.slug, o]));
  const focusOffer = offerBySlug[focusSlug];
  const linkedSlugs = new Set(blocks.filter(b => b.type === 'flash' && offerBySlug[b.content?.offer_slug]).map(b => b.content.offer_slug));
  const freeOffers = offers.filter(o => o.slug === focusSlug || !linkedSlugs.has(o.slug)).sort((a, b) => (b.slug === focusSlug) - (a.slug === focusSlug));

  useEffect(() => {
    if (loading || !offers.length) return undefined;
    setActiveSlug(s => s || freeOffers[0]?.slug || offers[0].slug);
    if (focusOffer) window.scrollTo({ top: 0 });
    if (!('IntersectionObserver' in window)) return undefined;
    const io = new IntersectionObserver(entries => {
      const visible = entries.filter(e => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (visible) setActiveSlug(visible.target.dataset.offerSlug);
    }, { threshold: [0.25, 0.5, 0.75] });
    document.querySelectorAll('[data-offer-slug]').forEach(el => io.observe(el));
    return () => io.disconnect();
  }, [loading, offers.length]);

  const getProductInfo = useCallback((block) => {
    if (block._singleProduct) {
      return { name: block._singleProduct.name, price: block._singleProduct.promo_price || block._singleProduct.original_price || 0, image: block._singleProduct.image_url };
    }
    const c = block.content || {};
    if (block.type === 'flash') return { name: c.product_name || block.title, price: c.promo_price || 0, image: c.image_url };
    if (block.type === 'bundle') return { name: block.title, price: c.bundle_price || 0, image: c.image_url };
    return { name: block.title, price: 0 };
  }, []);

  const handleOrder = async (payByCard = false) => {
    if (!form.first_name || !form.email || !form.address) { alert('Remplissez au minimum: prénom, email et adresse'); return; }
    setSubmitting(true);
    const info = getProductInfo(orderModal);
    const qty = form.quantity || 1;
    const items = [{ name: info.name, price: info.price, quantity: qty, image: info.image || '' }];
    const totalAmount = info.price * qty;

    if (payByCard) {
      try {
        const res = await fetch(`${BACKEND_URL}/api/payments/create-checkout`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items, total_amount: totalAmount, currency: 'chf',
            customer_name: `${form.first_name} ${form.last_name}`,
            customer_email: form.email, customer_phone: form.phone,
            shipping_address: { street: form.address, postal_code: form.postal_code, city: form.city, country: 'Suisse' },
            source: 'promotions_page',
          })
        });
        const d = await res.json();
        if (d.checkout_url || d.url) {
          trackMeta('InitiateCheckout', { value: totalAmount, currency: 'CHF', num_items: qty, content_type: 'product' });
          window.location.href = d.checkout_url || d.url; return;
        }
        alert('Erreur: ' + (d.detail || JSON.stringify(d)));
      } catch (e) { alert('Erreur de connexion: ' + e.message); }
      setSubmitting(false);
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/promotions/order`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, items, total_amount: totalAmount })
      });
      const d = await res.json();
      setSuccess({ type: 'order', message: d.message || 'Commande envoyée', number: d.order_number });
      setOrderModal(null);
    } catch (e) { alert('Erreur: ' + e.message); }
    setSubmitting(false);
  };

  const handleEmailRequest = async () => {
    if (!form.first_name || !form.email) { alert('Remplissez au minimum: prénom et email'); return; }
    setSubmitting(true);
    const info = getProductInfo(emailModal);
    try {
      const res = await fetch(`${BACKEND_URL}/api/promotions/request-email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ first_name: form.first_name, last_name: form.last_name, email: form.email, message: form.message, product_name: info.name, product_price: info.price })
      });
      const d = await res.json();
      setSuccess({ type: 'email', message: d.message || 'Demande envoyée' });
      setEmailModal(null);
    } catch (e) { alert('Erreur: ' + e.message); }
    setSubmitting(false);
  };

  const resetForm = () => setForm({ first_name: '', last_name: '', email: '', phone: '', address: '', postal_code: '', city: '', quantity: 1, message: '' });

  if (paymentSession) return (
    <PromoPaymentReturn sessionId={paymentSession} onPaid={cart.clear}
      onDone={() => { window.history.replaceState({ page: 'promotions' }, '', '/?page=promotions'); setPaymentSession(null); }} />
  );
  if (loading) return (
    <div className="max-w-7xl mx-auto px-3 py-4" data-testid="promo-loading">
      <div className="rounded-3xl overflow-hidden border border-white/[0.07] bg-[#0F0F10] animate-pulse">
        <div className="h-[270px] bg-zinc-800/60" />
        <div className="p-4 space-y-3"><div className="h-6 w-3/4 bg-zinc-800 rounded" /><div className="h-4 w-1/2 bg-zinc-800 rounded" /><div className="h-10 w-1/3 bg-zinc-800 rounded" /><div className="h-12 bg-zinc-800 rounded-xl" /></div>
      </div>
    </div>
  );
  if (loadError && offers.length === 0) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="promo-load-error">
      <p className="text-white text-lg font-semibold">Les offres n'ont pas pu être chargées</p>
      <p className="text-zinc-400 text-sm max-w-sm">Vérifiez votre connexion puis réessayez.</p>
      <PromoFallbackActions onRetry={() => { setLoading(true); loadAll(); }} onNavigate={onNavigate} />
    </div>
  );
  if (blocks.length === 0 && offers.length === 0) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="promo-empty">
      <p className="text-white text-lg font-semibold">Aucune offre pour le moment</p>
      <p className="text-zinc-400 text-sm max-w-sm">Revenez bientôt ou découvrez tout notre catalogue.</p>
      <PromoFallbackActions onRetry={() => { setLoading(true); loadAll(); }} onNavigate={onNavigate} />
    </div>
  );

  // Success screen
  if (success) return (
    <div className="min-h-[60vh] flex items-center justify-center px-6" data-testid="promo-success">
      <div className="bg-zinc-900 rounded-2xl p-12 text-center max-w-md border border-zinc-800">
        <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
          {success.type === 'order' ? <ShoppingCart className="w-8 h-8 text-white" /> : <Mail className="w-8 h-8 text-white" />}
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">{success.type === 'order' ? 'Commande envoyée !' : 'Demande envoyée !'}</h2>
        <p className="text-zinc-400 mb-1">{success.message}</p>
        {success.number && <p className="text-amber-400 font-mono text-sm mb-4">Ref: {success.number}</p>}
        <p className="text-zinc-500 text-sm mb-6">Nous vous contacterons rapidement par email.</p>
        <button onClick={() => { setSuccess(null); resetForm(); }} className="bg-amber-500 text-black px-8 py-3 rounded-lg font-bold hover:bg-amber-400" data-testid="promo-success-continue">Continuer</button>
        <div className="mt-4 p-3 bg-green-900/30 border border-green-800 rounded-xl">
          <p className="text-green-300 text-xs">Astuce : Créez un compte pro pour 5% de remise supplémentaire sur toutes vos commandes !</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-[60vh] bg-black text-[#F2EFE8] pb-32 md:pb-12" data-testid="promotions-page">
      <style>{PROMO_CSS}</style>
      {offers.length > 0 && <PromoHeader bannerText={settings.banner_text} />}
      {/* Render blocks */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 pt-3 md:pt-5 pb-8 space-y-6 md:space-y-10">
        {freeOffers.length > 0 && (
          <section className="space-y-5 md:space-y-4" data-testid="promo-offers-section">
            {freeOffers.map((o, i) => <PromoOfferCard key={o.slug} offer={o} cart={cart} index={i} eager={i === 0} highlighted={o.slug === focusSlug} onAdded={(_, q, u) => handleAdded(o.slug, q, u)} onEdit={() => setCartOpen(true)} onRequest={setRequestOffer} {...qtyProps(o.slug)} />)}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 rounded-2xl border border-white/[0.07] bg-[#0F0F10] px-5 py-4" data-testid="promo-request-banner">
              <p className="text-sm text-zinc-300">Grosse quantité, prix de projet ou autres conditions de paiement ?</p>
              <button onClick={() => setRequestOffer(undefined)} className="h-11 px-5 rounded-xl border border-[#C9A84C]/60 text-[#C9A84C] text-sm font-semibold hover:bg-[#C9A84C]/10" data-testid="promo-request-offer-btn">Demander une offre</button>
            </div>
          </section>
        )}
        {blocks.map((block) => {
          const linkedOffer = block.type === 'flash' && offerBySlug[block.content?.offer_slug];
          if (linkedOffer && linkedOffer.slug === focusSlug) return null;
          if (linkedOffer) return <LinkedFlashBlock key={block.id} block={block} offer={linkedOffer} cart={cart} highlighted={false} onAdded={(_, q, u) => handleAdded(linkedOffer.slug, q, u)} onEdit={() => setCartOpen(true)} onRequest={setRequestOffer} {...qtyProps(linkedOffer.slug)} />;
          switch (block.type) {
            case 'banner': return <BannerBlock key={block.id} block={block} />;
            case 'flash': return <FlashBlock key={block.id} block={block} onOrder={(b) => { resetForm(); setOrderModal(b); }} onEmailRequest={(b) => { resetForm(); setEmailModal(b); }} />;
            case 'grid': return <GridBlock key={block.id} block={block} onOrder={(b) => { resetForm(); setOrderModal(b); }} onEmailRequest={(b) => { resetForm(); setEmailModal(b); }} />;
            case 'pdf': return <PdfBlock key={block.id} block={block} />;
            case 'bundle': return <BundleBlock key={block.id} block={block} onOrder={(b) => { resetForm(); setOrderModal(b); }} onEmailRequest={(b) => { resetForm(); setEmailModal(b); }} />;
            default: return null;
          }
        })}
        <PromoFaq faq={settings.faq} />
      </div>
      {!cartOpen && <MobileBuyBar offer={offerBySlug[activeSlug]} cart={cart} onAdded={(_, q, u) => handleAdded(activeSlug, q, u)} onEdit={() => setCartOpen(true)} onRequest={setRequestOffer} {...qtyProps(activeSlug)} />}

      {cart.count > 0 && !cartOpen && (
        <button onClick={() => setCartOpen(true)} className="fixed bottom-[104px] md:bottom-5 right-4 md:right-5 z-50 bg-[#C9A84C] text-black rounded-full pl-5 pr-6 h-12 md:h-14 font-bold shadow-2xl flex items-center gap-2 hover:bg-amber-400 active:scale-95 transition-[background-color,transform]" data-testid="promo-cart-open">
          <ShoppingCart className="w-5 h-5" /> Panier <span className="bg-black text-amber-400 rounded-full min-w-[28px] h-7 px-2 flex items-center justify-center text-sm" data-testid="promo-cart-count">{cart.count}</span>
        </button>
      )}
      <PromoCartDrawer open={cartOpen} onClose={() => setCartOpen(false)} cart={cart} offers={offers} />
      {requestOffer !== null && <OfferRequestModal offer={requestOffer} offers={offers} onClose={() => setRequestOffer(null)} />}

      {/* ====== ORDER MODAL (Guest Checkout) ====== */}
      {orderModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setOrderModal(null)} data-testid="order-modal">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-lg font-bold text-white flex items-center gap-2"><ShoppingCart className="w-5 h-5 text-amber-400" /> Commander</h3>
              <button onClick={() => setOrderModal(null)} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              {/* Product summary */}
              <div className="bg-zinc-800 rounded-xl p-4 flex items-center gap-4">
                {getProductInfo(orderModal).image && <img src={getProductInfo(orderModal).image} alt="" className="w-16 h-16 object-contain bg-white rounded-lg p-1" />}
                <div className="flex-1">
                  <p className="text-white font-medium">{getProductInfo(orderModal).name}</p>
                  <p className="text-amber-400 font-bold">CHF {Number(getProductInfo(orderModal).price).toFixed(2)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setForm(f => ({ ...f, quantity: Math.max(1, f.quantity - 1) }))} className="w-7 h-7 bg-zinc-700 rounded flex items-center justify-center text-white"><Minus className="w-3 h-3" /></button>
                  <span className="text-white font-bold w-8 text-center">{form.quantity}</span>
                  <button onClick={() => setForm(f => ({ ...f, quantity: f.quantity + 1 }))} className="w-7 h-7 bg-zinc-700 rounded flex items-center justify-center text-white"><Plus className="w-3 h-3" /></button>
                </div>
              </div>
              <div className="text-right text-amber-400 font-bold text-lg">Total: CHF {(getProductInfo(orderModal).price * form.quantity).toFixed(2)}</div>

              {/* Form */}
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Prenom *" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} className="p-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm placeholder-zinc-500" data-testid="order-first-name" />
                <input placeholder="Nom" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} className="p-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm placeholder-zinc-500" data-testid="order-last-name" />
                <input placeholder="Email *" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="col-span-2 p-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm placeholder-zinc-500" data-testid="order-email" />
                <input placeholder="Telephone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="col-span-2 p-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm placeholder-zinc-500" data-testid="order-phone" />
                <input placeholder="Adresse *" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className="col-span-2 p-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm placeholder-zinc-500" data-testid="order-address" />
                <input placeholder="NPA" value={form.postal_code} onChange={e => setForm({ ...form, postal_code: e.target.value })} className="p-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm placeholder-zinc-500" data-testid="order-postal" />
                <input placeholder="Ville" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className="p-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm placeholder-zinc-500" data-testid="order-city" />
              </div>

              {/* Buttons */}
              <button onClick={() => handleOrder(false)} disabled={submitting} className="w-full bg-amber-500 text-black py-3 rounded-lg font-bold text-lg hover:bg-amber-400 disabled:opacity-40 flex items-center justify-center gap-2" data-testid="order-submit-invoice">
                <Send className="w-5 h-5" /> {submitting ? 'Envoi...' : 'Commander (paiement a reception)'}
              </button>
              <button onClick={() => handleOrder(true)} disabled={submitting} className="w-full bg-white text-zinc-900 py-3 rounded-lg font-bold text-lg hover:bg-zinc-100 disabled:opacity-40 flex items-center justify-center gap-2" data-testid="order-submit-card">
                <CreditCard className="w-5 h-5" /> Payer par carte maintenant
              </button>
              <p className="text-center text-zinc-600 text-xs">Paiement securise Stripe - Visa, Mastercard, PostFinance</p>
            </div>
          </div>
        </div>
      )}

      {/* ====== EMAIL REQUEST MODAL ====== */}
      {emailModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setEmailModal(null)} data-testid="email-modal">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl max-w-lg w-full shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-lg font-bold text-white flex items-center gap-2"><Mail className="w-5 h-5 text-amber-400" /> Demande par email</h3>
              <button onClick={() => setEmailModal(null)} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-zinc-800 rounded-xl p-4">
                <p className="text-white font-medium">{getProductInfo(emailModal).name}</p>
                {getProductInfo(emailModal).price > 0 && <p className="text-amber-400 text-sm">CHF {Number(getProductInfo(emailModal).price).toFixed(2)}</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Prenom *" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} className="p-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm placeholder-zinc-500" data-testid="email-first-name" />
                <input placeholder="Nom" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} className="p-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm placeholder-zinc-500" data-testid="email-last-name" />
                <input placeholder="Email *" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="col-span-2 p-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm placeholder-zinc-500" data-testid="email-email" />
              </div>
              <textarea placeholder="Votre message (optionnel)..." value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="w-full h-24 p-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm placeholder-zinc-500 resize-none" data-testid="email-message" />
              <button onClick={handleEmailRequest} disabled={submitting} className="w-full bg-amber-500 text-black py-3 rounded-lg font-bold hover:bg-amber-400 disabled:opacity-40 flex items-center justify-center gap-2" data-testid="email-submit">
                <Send className="w-5 h-5" /> {submitting ? 'Envoi...' : 'Envoyer la demande'}
              </button>
              <p className="text-center text-zinc-600 text-xs">Un email sera envoye a info@ledzone.ch avec votre demande</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
